import jwtDecode from 'jwt-decode';
import _ from 'lodash';
import { getIntl } from 'umi';

import { sphericalComputeDistanceBetween } from '@/services/maps';
import { CoreEnums } from '@/utils/domain/constants';
import { STORAGE_KEYS } from '@/utils/infrastructure/auth/sessionConstants';
import { getSessionItem, getSessionToken } from '@/utils/infrastructure/auth/sessionManager';
import { isJsonLike } from '../../../infrastructure';
import { toDataURL } from '../../../infrastructure/file-system';
import CoreFormula from '../formula';
import { safeEval } from '../safeEval';
import { JSON_DATA_TYPES, SEPARATORS } from './constants';

const { FormFieldBehavior, FormFieldType, FormFieldStringType } = CoreEnums;

export function mapMultiChoice(value) {
  if (!value) return [];

  const raw = _.isString(value) ? value : value.displayText;
  if (_.isNil(raw) || raw === '') return [];

  return _.split(
    raw,
    SEPARATORS.find((k) => _.includes(raw, k)),
  ).filter((item) => item != null && String(item).trim() !== '');
}

/**
 * Multi-select value as `{ label, value }[]` preserving item ids.
 * Needed in details/view so UserPicture can load photos (mapMultiChoice
 * only keeps display names).
 */
export function mapMultiChoiceLabelValue(value) {
  if (!value) return [];

  // Already shaped (e.g. prepareEditFieldValue / remount).
  if (_.isArray(value)) {
    return value
      .map((o) => {
        if (_.isString(o) || _.isNumber(o)) {
          const label = String(o);
          return label ? { label, value: label } : null;
        }
        if (!_.isPlainObject(o)) return null;
        const label = o.label ?? o.text;
        const id = o.value ?? o.id ?? label;
        if (_.isNil(label) && _.isNil(id)) return null;
        return { label: label ?? String(id), value: id };
      })
      .filter(Boolean);
  }

  try {
    const record = isJsonLike(value) ? JSON.parse(value) : value;
    if (_.isArray(record?.items)) {
      return record.items
        .filter((o) => o != null && !_.isNil(o.id))
        .map((o) => ({ label: o.text ?? o.label, value: o.id }));
    }
    // Single-item JSON accidentally passed into a multi field.
    if (_.isPlainObject(record) && !_.isNil(record.id)) {
      return [{ label: record.text ?? record.label, value: record.id }];
    }
  } catch {
    /* fall through */
  }

  return mapMultiChoice(value)
    .filter((label) => !!label)
    .map((label) => ({ label, value: label }));
}

/** Single selector value as `{ label, value }` with id (details/view). */
export function mapSingleChoiceLabelValue(value) {
  if (!value) return undefined;

  try {
    const record = isJsonLike(value) ? JSON.parse(value) : value;

    // Already shaped `{ label, value }`.
    if (
      _.isPlainObject(record) &&
      !_.has(record, 'items') &&
      !_.isNil(record.value) &&
      (record.label != null || record.text != null)
    ) {
      return { label: record.label ?? record.text, value: record.value };
    }

    const item = _.has(record, 'items') ? _.head(record.items) : record;
    if (!item || _.isNil(item.id)) {
      const label = item?.text || item?.label;
      return label ? { label, value: label } : undefined;
    }
    return { label: item.text ?? item.label, value: item.id };
  } catch {
    return undefined;
  }
}

export function fieldParentsMap(filters, fields) {
  return _.map(filters, (ff) => {
    const item = ff.sourceDynamicEntityField?.id
      ? _.find(fields, (o) => o.dynamicEntityField?.id === ff.sourceDynamicEntityField?.id)
      : ff.sourceField;

    if (!item) return null;
    return item.dynamicEntityField?.internalName ?? item.internalName;
  }).filter(Boolean);
}

export function fieldDependenciesMap(id, fields) {
  return _.filter(fields, (fld) =>
    fld.dynamicEntityField ? fld.dynamicEntityField.filters?.length > 0 : fld.filters?.length > 0,
  )
    .map((fld) => {
      if (fld.fieldBehavior === FormFieldBehavior.Locked) return;
      if (fld.dynamicEntityField) {
        const index = _.findIndex(
          fld.dynamicEntityField?.filters,
          (o) => o.sourceDynamicEntityField?.id === id,
        );

        return index >= 0 ? fld.dynamicEntityField.internalName : null;
      }

      const index = _.findIndex(fld.filters, (o) => o.sourceField?.id === id);
      return index >= 0 ? fld.internalName : null;
    })
    .filter(Boolean);
}

export function removeDefinedMethods(value) {
  try {
    if (value.startsWith('Number(') === true && value.endsWith(')') === true) {
      // eslint-disable-next-line no-param-reassign
      value = value.substring(7);
      // eslint-disable-next-line no-param-reassign
      value = value.substring(0, value.length - 1);
    }
  } catch (e) {}

  try {
    if (value.startsWith('Date(') === true && value.endsWith(')') === true) {
      // eslint-disable-next-line no-param-reassign
      value = value.substring(5);
      // eslint-disable-next-line no-param-reassign
      value = value.substring(0, value.length - 1);
    }
  } catch (e) {}

  return value;
}

// Helper function to determine if a field type is numeric
function isNumericType(type) {
  return (
    type === FormFieldType.Int ||
    type === FormFieldType.Decimal ||
    type === FormFieldStringType.Int ||
    type === FormFieldStringType.Int32 ||
    type === FormFieldStringType.Decimal
  );
}

export function parseFormulaFields(formulae = '', fields = []) {
  try {
    const formulaeFields = [];
    const matches = formulae.match(/\{\{.*?\}\}/g);

    if (!_.isNil(matches) && matches.length > 0) {
      _.each(matches, (str, i) => {
        const name = removeDefinedMethods(str.replace('{{', '').replace('}}', ''));
        const isAlreadyIn = formulaeFields.some((n) => {
          const existingName = _.isString(n) ? n : n.name;
          return existingName === name;
        });

        if (!isAlreadyIn) {
          if (fields && fields.length > 0) {
            const field = _.find(
              fields,
              (o) => (o.dynamicEntityField?.internalName || o.internalName) === name,
            );

            const fieldType = field?.dynamicEntityField?.type || field?.type;
            formulaeFields.push({
              name,
              type: fieldType,
              isNumeric: isNumericType(fieldType),
            });
          } else {
            formulaeFields.push(name);
          }
        }
      });
    }

    return formulaeFields;
  } catch (e) {
    return [];
  }
}

export function isUsedInFormulae(internalName, fields = []) {
  return _.some(fields, (f) => _.includes(f.formula, internalName));
}

export function tableWithCalculatedColumns(tableId, fields = []) {
  return _.filter(fields, (o) => o.sourceTable?.id === tableId).map((o) => o.internalName);
}

export function defaultFieldFilters(filters, fields, formId) {
  let fieldFilters = _.filter(filters, (fld) => fld.sourceForm?.id === formId);

  if (!fieldFilters.length) return;

  fieldFilters = _.map(fieldFilters, (fld) => {
    const item = fld.sourceDynamicEntityField?.id
      ? _.find(fields, (cd) => cd.dynamicEntityField?.id === fld.sourceDynamicEntityField?.id)
      : fld.sourceField;

    return {
      filterId: fld.id,
      parentTableFieldId: fld.sourceField?.parentTable?.id,
      internalName: item?.dynamicEntityField?.internalName ?? item?.internalName,
      format: CoreFormula._CleanMomentFormat(item?.displayTextDynamicEntityField?.format),
      type: item?.displayTextDynamicEntityField?.type || item?.type,
    };
  });

  return { fieldFilters };
}

export function instanceDataFieldValue(jsonSelectData, value, dependency, isDetails) {
  const record = isJsonLike(value) ? JSON.parse(value) : value;
  const item = _.has(record, 'items') ? _.head(record.items) : record;

  if ((!dependency || jsonSelectData) && !isDetails) {
    return {
      label: item.text || item.label,
      value: item.id,
      jsonSelectData,
      searchValue: item.searchValue,
    };
  }

  return item?.text || item?.label;
}

export function getCurrentUser(type, options = {}) {
  if (
    options?.userDisplayValue === CoreEnums.UserDisplayValue.ExtraField ||
    options?.userDisplayValue === CoreEnums.UserDisplayStringValue.ExtraField
  ) {
    return null;
  }

  try {
    const user = JSON.parse(getSessionItem(STORAGE_KEYS.AUTH_USER) || 'null');
    const primary = jwtDecode(getSessionToken()?.accessToken);

    if (!primary) return null;

    const displayValue = {
      [CoreEnums.UserDisplayValue.FullName]: primary.userFullName,
      [CoreEnums.UserDisplayValue.Email]: primary.userEmail,
      [CoreEnums.UserDisplayValue.Position]: primary.positionName,
      [CoreEnums.UserDisplayValue.Area]: primary.areaName,

      [CoreEnums.UserDisplayStringValue.FullName]: primary.userFullName,
      [CoreEnums.UserDisplayStringValue.Email]: primary.userEmail,
      [CoreEnums.UserDisplayStringValue.Position]: primary.positionName,
      [CoreEnums.UserDisplayStringValue.Area]: primary.areaName,
    };

    const label = displayValue[options?.userDisplayValue] || primary.userFullName;

    if (JSON_DATA_TYPES.includes(type)) {
      return {
        label,
        value: primary.primarysid || user.id,
        jsonSelectData: true,
      };
    }

    return label;
  } catch {
    return null;
  }
}

export async function preloadAttachmentFiles(data) {
  if (!data?.attachments?.length) return null;

  const filePromises = _.map(data.attachments, (item) => {
    if (!item.downloadUrl) return Promise.resolve(null);
    return toDataURL(item.downloadUrl, item.fileName);
  });

  return Promise.allSettled(filePromises).then((res) =>
    _.filter(res, { status: 'fulfilled' })
      .map((res) => res.value)
      .filter(Boolean),
  );
}

export function formulaFields(name, fields = []) {
  return _.filter(
    fields,
    (o) => o.fieldBehavior !== FormFieldBehavior.Locked && o.formula?.includes(name),
  )
    .filter(Boolean)
    .map((o) => o.internalName);
}

export function formulaBodySetup(formula, fields = []) {
  const formulaFields = parseFormulaFields(formula, fields);
  return {
    formula,
    formulaFields,
    disabled: true,
    ...(!formulaFields.length && {
      value: (() => {
        try {
          return safeEval(formula, {});
        } catch (e) {}
      })(),
    }),
  };
}

export function mapFieldLocationFilters(filters = []) {
  const locations = [];

  filters?.forEach((item) => {
    if (item.fixedValue) {
      locations.push({
        allowedDistance: item.allowedDistance,
        lat: item.fixedValue.lat,
        lng: item.fixedValue.long,
      });
    }
  });

  return locations;
}

export const checkLocationDistance = (location, locationFilters = []) => {
  let isValid = !!location;

  if (locationFilters.length > 0) {
    const { lat, long: lng } = location || {};
    for (const filter of locationFilters) {
      isValid = sphericalComputeDistanceBetween(filter, { lat, lng });
      if (!isValid) break;
    }
  }

  if (isValid) return Promise.resolve();
  return Promise.reject(getIntl().formatMessage({ id: 'global.text.message.outOfBoundLocation' }));
};

export const checkLikertSelection = (value, rows = [], options = {}) => {
  if (options?.required) {
    const err = getIntl().formatMessage({ id: 'form.error.question.required' });

    if (!value || !value?.items?.length) {
      return Promise.reject(new Error(err));
    }

    if (options?.allowMultiple) {
      const keys = Object.keys(value);
      const allSelected = keys.every((k) => value[k].length > 0);
      return keys.length === rows.length && allSelected
        ? Promise.resolve()
        : Promise.reject(new Error(err));
    }

    return value.items?.length === rows.length ? Promise.resolve() : Promise.reject(new Error(err));
  }

  return Promise.resolve();
};
